import {
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons"
import { Button, Flex, Form, Input, Select, Table, Typography } from "antd"
import { listStaffOrders } from "~/api/staff"
import {
  MOBILE_PHONE_PATTERN,
  OrderStatus,
  OrderType,
} from "~/common/constants"
import { optionalValue } from "~/common/utils"
import { orderStatusLabel, orderTypeLabel } from "~/pages/Staff/labels"
import { usePagedQuery } from "~/pages/Staff/usePagedQuery"
import { columns } from "./columns"
import { useOrderExport } from "./useOrderExport"
import styles from "./index.css"

const { Text, Title } = Typography

const orderTypeOptions = [
  OrderType.levelPackage,
  OrderType.walletPackage,
  OrderType.bulkWalletPackage,
].map((value) => ({ label: orderTypeLabel[value], value }))

const orderStatusOptions = [
  OrderStatus.succeeded,
  OrderStatus.waitPay,
  OrderStatus.closed,
  OrderStatus.cancelled,
  OrderStatus.init,
].map((value) => ({ label: orderStatusLabel[value], value }))

const initialFilters: API.Staff.OrderFilterParams = {
  status: OrderStatus.succeeded,
}

export const StaffOrderList = () => {
  const [form] = Form.useForm<API.Staff.OrderFilterParams>()
  const { filters, loading, search, refresh, tableProps } = usePagedQuery<
    API.Staff.OrderFilterParams,
    API.Staff.OrderListItem
  >({
    fetchPage: listStaffOrders,
    initialFilters,
    errorMessage: "订单列表加载失败，请稍后重试",
  })
  const { exporting, exportOrders } = useOrderExport(filters)

  const searchOrders = (values: API.Staff.OrderFilterParams) => {
    search({
      user_mobile: optionalValue(values.user_mobile),
      order_type: values.order_type,
      status: values.status ?? null,
    })
  }

  const resetSearch = () => {
    form.resetFields()
    search(initialFilters)
  }

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        align="center"
        justify="space-between"
        gap={12}
        wrap
      >
        <Flex
          vertical
          gap={6}
        >
          <Flex
            align="center"
            gap={8}
          >
            <UnorderedListOutlined className={styles.titleIcon} />
            <Title level={2}>订单列表</Title>
          </Flex>
          <Text type="secondary">
            查看充值和专业版订购订单，默认展示支付成功的订单，支持按用户手机号、订单类型和订单状态筛选。
          </Text>
        </Flex>
        <Button
          icon={<DownloadOutlined />}
          loading={exporting}
          disabled={loading}
          onClick={() => void exportOrders()}
          type="primary"
        >
          导出数据明细
        </Button>
      </Flex>

      <section className={styles.panel}>
        <Flex
          vertical
          gap={16}
        >
          <Flex
            align="start"
            justify="space-between"
            gap={12}
            wrap
          >
            <Form<API.Staff.OrderFilterParams>
              form={form}
              initialValues={initialFilters}
              layout="inline"
              onFinish={searchOrders}
            >
              <Flex
                gap={12}
                wrap
              >
                <Form.Item
                  className={styles.filterItem}
                  label="用户手机号"
                  name="user_mobile"
                  normalize={(value: string) => value.trim()}
                  rules={[
                    {
                      pattern: MOBILE_PHONE_PATTERN,
                      message: "请输入正确的手机号",
                    },
                  ]}
                >
                  <Input
                    className={styles.phoneInput}
                    allowClear
                    maxLength={11}
                    placeholder="请输入用户手机号"
                  />
                </Form.Item>
                <Form.Item
                  className={styles.filterItem}
                  label="订单类型"
                  name="order_type"
                >
                  <Select
                    className={styles.typeSelect}
                    allowClear
                    options={orderTypeOptions}
                    placeholder="全部类型"
                  />
                </Form.Item>
                <Form.Item
                  className={styles.filterItem}
                  label="订单状态"
                  name="status"
                >
                  <Select
                    className={styles.typeSelect}
                    allowClear
                    options={orderStatusOptions}
                    placeholder="全部状态"
                  />
                </Form.Item>
                <Flex gap={8}>
                  <Button
                    htmlType="submit"
                    icon={<SearchOutlined />}
                    loading={loading}
                    type="primary"
                  >
                    查询
                  </Button>
                  <Button onClick={resetSearch}>重置</Button>
                </Flex>
              </Flex>
            </Form>
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={refresh}
            >
              刷新
            </Button>
          </Flex>
          <Text type="secondary">
            导出当前已查询条件下的全部订单，不受分页限制。交易时间为支付时间；无权益周期的订单显示为「-」。
          </Text>
          <Table<API.Staff.OrderListItem>
            columns={columns}
            {...tableProps}
            locale={{ emptyText: "暂无符合条件的订单" }}
            rowKey="order_no"
            scroll={{ x: 1930 }}
          />
        </Flex>
      </section>
    </Flex>
  )
}
